import ReCAPTCHA from "react-google-recaptcha"
import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import css from "./Footer.scss"
import GAEvents from "./GAEvents"
import { isPwaUrl } from "@lib/urlMatchUtility"
import PrimaryButton from "@components/Button/Button"
import { fetchData, sendDownloadLink } from "./reducer"
import Separator from "@components/Separator/Separator"
import Accordion from "@components/Accordion/Accordion"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"

const StatsSection = ({ header, stats, statsOrder }) => {
    const orderedStats = statsOrder.map((stat, idx) => {
        const statData = stats[stat]
        return (
            <div key={idx} className="flexColumn alignCenter">
                <div className="l1SemiBold">{statData?.value}</div>
                <div className="l5Regular">{statData?.stat?.mobile}</div>
            </div>
        )
    })

    return (
        <>
            <div className="flexColumn alignCenter">
                {header && <div className={`${css.statsHeader} l2Regular`}>{header}</div>}
                <div className="col-16 flex justifyBetween marginTop-16">{orderedStats}</div>
            </div>
            <div className="col-22">
                <Separator size="xs" marginTop={16} />
            </div>
        </>
    )
}

const DownloadAppSMS = ({ header, placeholder }) => {
    const dispatch = useDispatch()
    const [input, setInput] = useState("")
    const [showCaptcha, setShowCaptcha] = useState(false)
    const { showSMSMessage } = useSelector((state) => state.footerReducer)

    const onCaptchaChange = (e) => {
        const payload = {
            mobile: input,
            recaptchaResponse: e,
        }
        GAEvents.onClick("", "Sms Link")
        dispatch(sendDownloadLink(payload))
        setShowCaptcha(false)
        setInput("")
    }

    return (
        <div className="flex alignCenter marginTop-24">
            <div className="l3Regular">{header}</div>
            <input
                type="tel"
                value={input}
                maxLength={10}
                placeholder={placeholder}
                onChange={(e) => setInput(e.target.value)}
                className={`insetDivider ${css.downloadInput}`}
            />
            <div>
                <PrimaryButton
                    label="Send Link"
                    onClick={() => {
                        dispatch(sendDownloadLink({ mobile: "9999999999" }))
                        if (input.length === 10) {
                            setShowCaptcha(true)
                            GAEvents.onClick("", "Google Recaptcha Show")
                        }
                    }}
                />
            </div>
            {showCaptcha && (
                <div className="col-2">
                    <ReCAPTCHA sitekey={process.env.RECAPTCHA_KEY} onChange={onCaptchaChange} />
                </div>
            )}
            {showSMSMessage && (
                <div className="flex alignCenter gutter-only">
                    <ImageLoader src="https://img.1mg.com/images/thank_you.svg" />
                    <div className="l5SemiBold gutter-only">SMS Sent</div>
                </div>
            )}
        </div>
    )
}

const OrderedLinks = ({ sectionsOrder, sectionsLinks }) => {
    return sectionsOrder.map((section) => {
        const sectionLinks = sectionsLinks[section]
        return (
            <div key={sectionLinks.header}>
                <div className="l5Medium">{sectionLinks.header}</div>
                <div className="marginTop-16">
                    {sectionLinks.links.map((link) => {
                        return (
                            <ConditionalLink
                                key={link.url}
                                to={link.url}
                                isExternal={!isPwaUrl(link.url)}
                                className="noAnchorColor"
                                onClick={() => GAEvents.onClick(link.url, sectionLinks.header)}
                            >
                                <div className="smallRegular textSecondary">{link.text}</div>
                            </ConditionalLink>
                        )
                    })}
                </div>
            </div>
        )
    })
}

const SocialMediaLinks = ({ socialData, signUpData, isLoggedIn }) => {
    const location = useLocation()
    return (
        <div>
            <div className="l5Medium">{socialData.main_header}</div>
            <div className="bodyRegular textSecondary marginTop-12">{socialData.text}</div>
            <div className="flex marginTop-8">
                {socialData.links?.map((link) => (
                    <div key={link.url} className={css.socialMediaLink}>
                        <ConditionalLink
                            to={link.url}
                            isExternal={!isPwaUrl(link.url)}
                            onClick={() => GAEvents.onClick(link.name, socialData.text)}
                        >
                            <ImageLoader src={link.icon} width="27px" height="26px" />
                        </ConditionalLink>
                    </div>
                ))}
            </div>
            {signUpData && !isLoggedIn && (
                <div>
                    <div className="bodyRegular textSecondary">{signUpData.text}</div>
                    <div className={css.sendLinkButton}>
                        <ConditionalLink
                            to={`/?signup=true&followup=${location.pathname}`}
                            isExternal={!isPwaUrl(`/?signup=true&followup=${location.pathname}`)}
                        >
                            <PrimaryButton label={signUpData.cta} />
                        </ConditionalLink>
                    </div>
                </div>
            )}
        </div>
    )
}

const DownloadAppLinks = ({ downloadData }) => {
    return (
        <div className="flexColumn">
            <div className="bodySemiBold">{downloadData.text}</div>
            {downloadData.data?.map((item) => (
                <ConditionalLink
                    key={item.url}
                    to={item.url}
                    isExternal={!isPwaUrl(item.url)}
                    onClick={() => GAEvents.onDownloadAppLink(item.name)}
                >
                    <ImageLoader src={item.icon} className="marginTop-12" />
                </ConditionalLink>
            ))}
        </div>
    )
}

const LinksSection = ({ sectionsLinks, sectionsOrder, socialData, signUpData, downloadData }) => {
    const isLoggedIn = useSelector((state) => state.shellReducer)
    return (
        <>
            <div className="col-22 flex justifyBetween marginTop-40">
                {sectionsOrder && sectionsLinks && (
                    <OrderedLinks sectionsOrder={sectionsOrder} sectionsLinks={sectionsLinks} />
                )}
                {socialData && signUpData && (
                    <SocialMediaLinks
                        socialData={socialData}
                        signUpData={signUpData}
                        isLoggedIn={isLoggedIn}
                    />
                )}
                {downloadData && <DownloadAppLinks downloadData={downloadData} />}
            </div>
            <div className="col-22">
                <Separator size="xs" marginTop={32} />
            </div>
        </>
    )
}

const TrustSection = ({ data }) => {
    return (
        <>
            <div className="col-22 flex justifyBetween alignCenter marginTop-40">
                {data.map((item) => (
                    <div key={item.header} className="flex alignCenter">
                        <ImageLoader src={item.image} alt={item.header} />
                        <div>
                            <div className="l1SemiBold">{item.header}</div>
                            <div className="smallRegular textSecondary">{item.text}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="col-22">
                <Separator size="xs" marginTop={32} />
            </div>
        </>
    )
}

const LegitSection = ({ data }) => {
    return (
        <>
            <div className="flexColumn alignCenter marginTop-40">
                <div className="col-6 flex justifyBetween">
                    {data.icons?.map((icon) => (
                        <ConditionalLink to={icon.url} key={icon.url} isExternal={!isPwaUrl(icon.url)}>
                            <ImageLoader src={icon.icon} alt={icon.name} height="84px" />
                        </ConditionalLink>
                    ))}
                </div>
                <div className="l5SemiBold marginTop-16">{data.text}</div>
            </div>
            <div className="col-24">
                <Separator size="xs" marginTop={32} />
            </div>
        </>
    )
}

const KnowMoreSection = ({ header, data }) => {
    return (
        <div className="col-24">
            <Accordion title={header}>
                {data.map((section) => (
                    <div key={section.header} className="marginTop-12">
                        <div className="l5SemiBold">{section.header}</div>
                        <div className="bodyRegular">{section.description}</div>
                    </div>
                ))}
            </Accordion>
        </div>
    )
}

const CopyrightSection = ({ text }) => {
    return (
        <>
            <div className="flex justifyCenter bodySemiBold textSecondary marginTop-16 paddingBoth-16-0">
                {text}
            </div>
        </>
    )
}

function Footer() {
    const dispatch = useDispatch()
    const { footerData } = useSelector((state) => state.footerReducer)

    const {
        headers,
        platformStatsOrder,
        statsData,
        smsAppLink,
        domainsSectionsOrder,
        sectionsLinks,
        connectData,
        downloadApp,
        trustSection,
        legitScript,
        signupData,
        knowMoreSection,
        copyrightText,
    } = footerData

    useEffect(() => {
        dispatch(fetchData())
    }, [])

    return (
        <>
            <div className={`container-fluid flexColumn alignCenter`}>
                {headers && statsData && platformStatsOrder && (
                    <StatsSection
                        header={headers.header2}
                        stats={statsData}
                        statsOrder={platformStatsOrder.desktop}
                    />
                )}
                {smsAppLink && (
                    <DownloadAppSMS
                        header={smsAppLink.get_link_text}
                        placeholder={smsAppLink.input_placeholder}
                    />
                )}
            </div>
            <Separator size="xs" marginTop={24} />
            <div className={`flexColumn alignCenter ${css.coloredSection}`}>
                {sectionsLinks && domainsSectionsOrder && connectData && signupData && downloadApp && (
                    <LinksSection
                        sectionsLinks={sectionsLinks}
                        sectionsOrder={domainsSectionsOrder.pharmacy}
                        socialData={connectData}
                        signUpData={signupData}
                        downloadData={downloadApp}
                    />
                )}
                {trustSection && <TrustSection data={trustSection} />}
                {legitScript && <LegitSection data={legitScript} />}
                {knowMoreSection && (
                    <KnowMoreSection header={knowMoreSection.header} data={knowMoreSection.data} />
                )}
                {copyrightText && <CopyrightSection text={copyrightText} />}
            </div>
        </>
    )
}

Footer.serverSideFunction = () => {
    return Promise.resolve()
}

Footer.setFooterData = () => {
    return Promise.resolve()
}

export default Footer
